import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  Chip,
  Collapse,
  Grid,
} from '@mui/material';
import { formatCurrency as formatCurrencyFromConstants } from '../../constants/currency';
import CascadingSidePanel from './CascadingSidePanel';
import DetailRow from '../../form/DetailRow';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { closeOrderSidePanel } from '../../store/slices/sidePanelSlice';
import { formatStatusText } from "../../utils/formatters";

interface Order {
  id: string;
  orderNumber?: string;
  orderNumberNew?: string;
  order_tracking_code?: string;
  namePlate?: string;
  customerName?: string;
  siteName?: string;
  status?: string;
  priority?: string;
  orderDate?: string;
  deliveryDate?: string;
  totalAmount?: number;
  products?: any[];
}

interface Site {
  id: string;
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  status?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  orders?: any[];
  totalOrders?: number;
  completedOrders?: number;
  pendingOrders?: number;
}

interface OrderSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  orders: Order[];
  onOrderSelect?: (order: Order) => void;
  title?: string;
  loading?: boolean;
  site:Site[];
  onBackToSites?: () => void;
  selectedOrderId?: string; // Track selected order
}

const OrderSidePanel: React.FC<OrderSidePanelProps> = ({
  isOpen,
  onClose,
  onCloseAll,
  orders,
  onOrderSelect,
  title = "",
  site,
  loading = false,
  onBackToSites,
  selectedOrderId
}) => {
  const dispatch = useDispatch();
  const [siteDetailsExpanded, setSiteDetailsExpanded] = useState(false);
  console.log('OrderSidePanel - orders:', orders);
  console.log('OrderSidePanel - loading:', loading);
  console.log('OrderSidePanel - orders length:', orders?.length);
  console.log('OrderSidePanel - title:', title);


  useEffect(()=>{
    console.log("site from order side panel", site)
  })

  const capitalizeWords = (str: string) => {
    return str.replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '';
    return formatCurrencyFromConstants(amount);
  };

  return (
    <CascadingSidePanel
      isOpen={isOpen}
      onClose={onClose}
      onCloseAll={onCloseAll}
      panelId="order-panel"
      title={title}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
          onClick={site ? () => setSiteDetailsExpanded(!siteDetailsExpanded) : undefined}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {onBackToSites && (
                <IconButton 
                  onClick={onBackToSites}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  ←
                </IconButton>
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {capitalizeWords(title)} - ({site?.namePlate || 'N/A'})
              </Typography>
              {site && (
                <IconButton 
                  size="small" 
                  sx={{ color: 'text.secondary' }}
                >
                  {siteDetailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
            </Box>
            <IconButton 
              // onClick={onCloseAll || onClose}
              onClick={()=>{
                dispatch(closeOrderSidePanel())
              }}
              size="small"
            >
              ✕
            </IconButton>
          </Box>

          {site && (
            <Collapse in={siteDetailsExpanded}>
              <Box sx={{ 
                p: 2, 
                pt: 0,
                // bgcolor: 'primary.light', 
                // color: 'primary.contrastText',
                borderRadius: '0 0 8px 8px'
              }}>
                <Grid 
                  container 
                  spacing={2} 
                  sx={{ mt: 1 }}
                >
                  {/* {customer.phone &&(<Grid item xs={12} sm={6}>
                    <DetailRow label="Phone" value={customer?.phone} />
                  </Grid>
                  )} */}
    
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Address" value={site?.address} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="City" value={site?.city} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Contact Person" value={site?.contactPerson} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Contact Phone" value={site?.contactPhone} />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          )}

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
             <Typography variant="h6" sx={{ fontWeight: 600,mb:1 }}>
                Order List
              </Typography>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '200px',
                textAlign: 'center'
              }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Loading orders...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we fetch the orders for this site.
                </Typography>
              </Box>
            ) : orders.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '200px',
                textAlign: 'center'
              }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No orders found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This site doesn't have any orders yet.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {orders.map((order, index) => {
                  const isSelected = selectedOrderId === order.id;
                  return (
                    <Card 
                      key={order.id || index}
                      sx={{ 
                        p: 1.5, 
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.12)' : 'action.hover',
                          transform: 'translateY(-1px)',
                          boxShadow: isSelected ? 3 : 1,
                          borderColor: 'primary.main'
                        },
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: isSelected ? 2 : 0,
                      }}
                      onClick={() => onOrderSelect?.(order)}
                    >
                      {/* Order Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: 'primary.main',
                            flexShrink: 0
                          }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {order.orderNumberNew || order.orderNumber || order.order_tracking_code || order.namePlate || `Order ${index + 1}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {order.priority && (
                            <Chip
                              label={capitalizeWords(order.priority)}
                              color={order.priority === 'high' ? 'error' : 
                                     order.priority === 'medium' ? 'warning' : 'default'}
                              size="small"
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                          )}
                          <Chip
                            label={formatStatusText(order.status || 'Pending')}
                            color={order.status === 'completed' ? 'success' : 
                                   order.status === 'in_progress' ? 'primary' : 'warning'}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                      </Box>

                      {/* Order Details */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {order.customerName && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Customer: {order.customerName}
                          </Typography>
                        )}
                        {order.siteName && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Site: {order.siteName}
                          </Typography>
                        )}
                        {order.orderDate && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Order Date: {formatDate(order.orderDate)}
                          </Typography>
                        )}
                        {order.deliveryDate && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Delivery: {formatDate(order.deliveryDate)}
                          </Typography>
                        )}
                        {order.totalAmount && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Amount: {formatCurrency(order.totalAmount)}
                          </Typography>
                        )}
                        {order.products && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Products: {order.products.length}
                          </Typography>
                        )}
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
    </CascadingSidePanel>
  );
};

export default OrderSidePanel;
